<template>
  <div class="form-group">
    <label
      for="confirmationCode"
      class="form-label"
      :class="{ 'fs-4': isSelfService }"
    >
      {{ labelName }}
    </label>
    <input
      id="confirmationCode"
      :type="inputType"
      :maxlength="maxLength"
      v-model="formConfirmationCode"
      class="form-control pj-input"
      :class="[
        { 'is-invalid': errorMessage },
        { 'fs-3 pj-ss-input': isSelfService },
      ]"
      @change="validate"
      aria-describedby="confirmationCodelHelp"
      :placeholder="placeholder"
      :disabled="disabled"
    />
    <div
      v-if="errorMessage"
      class="invalid-feedback"
      :class="{ 'fs-3': isSelfService }"
    >
      {{ errorMessage }}
    </div>
    <div id="confirmationCodeHelp" class="form-text"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  labelName: String,
  placeholder: String,
  disabled: {
    type: Boolean,
    default: false,
  },
  maxLength: {
    type: Number,
    default: 6,
  },
  minLength: {
    type: Number,
    default: 6,
  },
  alphanumeric: {
    type: Boolean,
    default: false,
  },
  extraValidation: {
    type: Function,
    default: null,
  },
  inputValue: {
    type: String,
    default: "",
  },
  inputType: {
    type: String,
    default: "text",
  },
  isSelfService: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["input-field-confirmation-code-emit"]);

const { t } = useI18n();
const formConfirmationCode = ref(props.inputValue);
const errorMessage = ref("");
const error = ref(false);

const codeFormat = (): void => {
  let regExp = new RegExp(`(\\d{0,${props.maxLength}})`);
  let regExpNon = new RegExp("\\D", "g");
  if (props.alphanumeric) {
    regExp = new RegExp(
      `[a-zA-Z0-9]{${props.minLength},${props.maxLength}}`,
      "i"
    );
    regExpNon = new RegExp("\\W", "g");
  }

  const confirmationCode = formConfirmationCode.value;
  if (typeof confirmationCode === "string") {
    const result: RegExpMatchArray | null = confirmationCode
      .replace(regExpNon, "")
      .match(regExp);
    if (result != null) {
      formConfirmationCode.value = result[0];
    }
  }
};

watchEffect(() => {
  if (props.inputValue !== undefined) {
    formConfirmationCode.value = props.inputValue;
  }
});

const isConfirmationCodeValid = (formConfirmationCode: string): boolean => {
  let regExpConfirm = new RegExp(`(\\d{${props.maxLength}})`);
  if (props.alphanumeric) {
    regExpConfirm = new RegExp(
      `[a-zA-Z0-9]{${props.minLength},${props.maxLength}}`,
      "i"
    );
  }
  let returnValue = regExpConfirm.test(formConfirmationCode);

  if (returnValue && props.extraValidation !== null) {
    returnValue = props.extraValidation(formConfirmationCode);
  }
  return returnValue;
};

const validate = (): boolean => {
  errorMessage.value = "";
  codeFormat();
  if (!formConfirmationCode.value) {
    errorMessage.value = t("SHARED.CONFIRMATION_CODE_ERROR_TEXT_REQUIRED");
    error.value = true;
    return false;
  } else if (!isConfirmationCodeValid(formConfirmationCode.value)) {
    errorMessage.value = t("SHARED.CONFIRMATION_CODE_ERROR_TEXT_NOT_VALID");
    error.value = true;
    return false;
  }
  emit("input-field-confirmation-code-emit", formConfirmationCode.value);
  return true;
};

const getInputFieldValue = () => {
  return formConfirmationCode.value;
};

defineExpose({
  validate,
  getInputFieldValue,
});
</script>
